import { useEffect } from 'react';
import { useTypedSelector } from '../../state/hooks';
import { CustomEventTypes } from '../../globals';
import ScopeCamera from '../../classes/Camera';
import Engine from '../../classes/Engine';
import { assert } from '../../classes/utilities/ErrorHandling';
import useEventListener from '../reactUtils/useEventListener';
import useCurrentStep from '../reactUtils/useCurrentStep';

export default function CameraPairingsHtmlRenderer(props: { 
	htmlDocument: Document | null | undefined, 
}) 
{
	useEventListener(CustomEventTypes.cameraRendered, handleCameraRendered, document);

	const cameraCanvasPairings = useTypedSelector<{ [cameraId: string]: string[]; }>(state => state.visageModule.cameraCanvasPairings);
	const step = useCurrentStep();

	useEffect(() => 
	{
		for (const [canmeraId, canvasIds] of Object.entries<string[]>(cameraCanvasPairings)) 
		{
			const scene = step?.scene;
			if (!scene) continue;

			const scopeCamera = ScopeCamera.getSceneCameras(scene).find(c => c.userData.guid === canmeraId);
			scopeCamera?.toggleUpdateCanvas(canvasIds.length > 0);
		}
	}, [cameraCanvasPairings, step]);

	function handleCameraRendered(e) 
	{
		if (!props.htmlDocument) return;

		const cameraId = e.detail.cameraId;
		if (!cameraId) return;

		const scopeCamera = ScopeCamera.getSceneCameras(Engine.getInstance().getCurrentScene()).find(c => c.userData.guid === cameraId);
		if (!scopeCamera) return;

		// it's a map of cameraId -> set of elementId
		const pairings = cameraCanvasPairings[cameraId];
		if (!pairings) return;

		for (const elementId of pairings) 
		{
			const htmlElement = props.htmlDocument.getElementById(elementId);
			if (!htmlElement) continue;

			// verify we got a canvas tag
			assert(htmlElement.tagName.toLowerCase() === "canvas", "non-canvas element registered as a camera canvas pairing");
			if (htmlElement.tagName.toLowerCase() !== "canvas") continue; // assert should throw but ts complains anyway

			const canvasElement = htmlElement as HTMLCanvasElement;
			const context = canvasElement.getContext('2d');
			assert(context, "canvas element has no context");
			if (!context) continue; // assert should throw but ts complains anyway

			context.clearRect(0, 0, canvasElement.width, canvasElement.height);
			context.drawImage(
				scopeCamera.canvas,
				0, 0, scopeCamera.canvas.width, scopeCamera.canvas.height,
				0, 0, canvasElement.width, canvasElement.height // destination rect
			);
			context.globalCompositeOperation = "source-over";
			context.lineWidth = 5;
			context.strokeStyle = "#000000";
			context.strokeRect(0, 0, canvasElement.width, canvasElement.height);
		}
	}

	return null;
}
